import React, { useCallback, useEffect, useState } from 'react';
import { EditIcon, NoData, SearchIconNew, ViewIconNew } from '../../theme/SvgIcons';
import { Button, Drawer, Input, Pagination, Table, Tooltip } from 'antd';
import {
  DarkText,
  EditIconBox,
  FlexWrapper,
  PaginationBox,
  ViewIconBox
} from '../../theme/common_style';
import Title from 'antd/es/typography/Title';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { checkPermission, debounce, getFullName } from '../../utils/common_functions';
import { StickyBox } from '../../utils/style';
import { useCurrentModule } from '../../hooks/useCurrentModule';
import CreateLeads from './CreateLeads';
import { getProposalSheet } from '../../services/api_collection';
import dayjs from 'dayjs';
import { actionTypeEnums } from '../../utils/constant';
import { updateActivityDrawer } from '../../redux/sidebar/SidebarSlice';
import LeadsActivity from './LeadsActivity';
import ViewLeads from './ViewLeads';
import FilterButton from '../../components/common/FilterButton';
import LeadFilters from './LeadFilters';
import styled from '@emotion/styled';

function Leads() {
  const dispatch = useDispatch();
  const [createLead, setCreateLead] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDetails, setEditDetails] = useState(null);
  const { permissions, user_details } = useSelector((state) => state?.userInfo?.data);

  const [viewLeadsModal, setViewLeadsModal] = useState(null);

  let permissionSection = useCurrentModule();
  const canCreate =
    user_details?.isBusinessUser ||
    checkPermission(permissionSection, actionTypeEnums.CREATE, permissions);
  const canUpdate =
    user_details?.isBusinessUser ||
    checkPermission(permissionSection, actionTypeEnums.UPDATE, permissions);

  const [sort, setSort] = useState({});
  const [search, setSearch] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(20);

  const activityDrawer = useSelector((state) => state?.sidebar?.isActivityDrawer);
  const [appliedFilter, setAppliedFilter] = useState(null);
  const [filterDrawer, setFilterDrawer] = useState(false);

  const handlePageChange = (page) => {
    setPage(page);
  };

  const handleSorting = (val) => {
    if (val?.field) {
      let order = val?.order == 'ascend' ? 'ASC' : 'DESC';
      setSort({ sortBy: val?.field, orderBy: order, ...val });
    } else {
      setSort({});
    }
    setPage(page);
  };

  const columns = [
    {
      title: 'S.NO',
      dataIndex: 'key',
      key: 'key',
      width: 60,
      render: (text) => <span>{(page - 1) * pageSize + text}.</span>
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: true,
      width: 120,
      render: (text) => <span>{dayjs(text).format('DD-MM-YYYY')}</span>
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: 150,
      sorter: true,
      render: (text) => (
        <span>{getFullName(text?.first_name, text?.middle_name, text?.last_name)}</span>
      )
    },
    {
      title: 'ID Name',
      dataIndex: 'billing_id_name',
      key: 'billing_id_name',
      width: 150,
      sorter: true,
      render: (text) => <span>{text?.name || 'N/A'}</span>
    },
    {
      title: 'Client',
      dataIndex: 'client_name',
      key: 'client_name',
      width: 150,
      render: (text) => <span>{text || 'N/A'}</span>
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 140,
      sorter: true,
      render: (text) => <span>{text || 'N/A'}</span>
    },
    {
      title: 'Job Title',
      dataIndex: 'job_title',
      key: 'job_title',
      width: 150,
      render: (text) => <span>{text || 'N/A'}</span>
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      width: 120,
      render: (text) => <span>{text?.length > 0 ? text.join(', ') : 'N/A'}</span>
    },
    {
      title: 'Proposal',
      dataIndex: 'prop_url',
      key: 'prop_url',
      width: 100,
      render: (text) =>
        text ? (
          <a href={text} target="_blank" rel="noreferrer">
            Link
          </a>
        ) : (
          'N/A'
        )
    },
    {
      title: 'Real Time Status',
      dataIndex: 'real_time_status',
      key: 'real_time_status',
      width: 180,
      render: (text) => {
        const displayText = text || 'N/A';
        const truncatedText =
          displayText.length > 19 ? `${displayText.substring(0, 19)}...` : displayText;

        return (
          <Tooltip title={displayText} placement="top">
            <span style={{ cursor: 'pointer' }}>{truncatedText}</span>
          </Tooltip>
        );
      }
    },
    {
      title: 'Action',
      dataIndex: 'action',
      fixed: 'right',
      key: 'action',
      width: 100,
      className: 'action-column',
      render: (_, data) => {
        return (
          <FlexWrapper justify={'start'} gap={'6px'} wrap={'unset'} cursor="pointer">
            <ViewIconBox
              onClick={() => {
                setViewLeadsModal(data);
              }}>
              <ViewIconNew />
            </ViewIconBox>
            {canUpdate && (
              <EditIconBox
                canUpdate={canUpdate}
                onClick={() => {
                  setCreateLead(true);
                  setEditDetails(data);
                }}>
                <EditIcon />
              </EditIconBox>
            )}
          </FlexWrapper>
        );
      }
    }
  ];

  const handleAddProject = () => {
    setCreateLead(true);
    setEditDetails(null);
  };

  const handleLeadList = async (search) => {
    try {
      setLoading(true);
      let params = new URLSearchParams();
      sort?.sortBy && params.append('sort_by', sort?.sortBy);
      sort?.orderBy && params.append('order', sort?.orderBy);
      search && params.append('search', search);

      appliedFilter?.platform?.length > 0 && params.append('platform', appliedFilter?.platform);
      appliedFilter?.date?.length > 0 &&
        params.append('start_date', dayjs(appliedFilter?.date?.[0]).format('YYYY-MM-DD'));
      appliedFilter?.date?.length > 0 &&
        params.append('end_date', dayjs(appliedFilter?.date?.[1]).format('YYYY-MM-DD'));
      appliedFilter?.ownerId && params.append('ownerId', appliedFilter?.ownerId);

      params.append('page', page);
      params.append('limit', pageSize);

      let res = await getProposalSheet(params);
      if (res?.statusCode === 200) {
        let array = (res?.data?.proposals || [])?.map((el, idx) => ({
          ...el,
          key: idx + 1
        }));
        setData(array);
        setTotalCount(res?.data?.pagination?.total);
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    } catch (error) {
      toast.error(error?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const optimizedFn = useCallback(debounce(handleLeadList), [sort, page, appliedFilter]);

  useEffect(() => {
    if (search !== null) {
      optimizedFn(search);
    } else {
      handleLeadList(search);
    }
  }, [sort, search, page, appliedFilter]);

  return (
    <div style={{ background: '#f3f6fc' }}>
      {filterDrawer && (
        <LeadFilters
          open={filterDrawer}
          onClose={() => setFilterDrawer(false)}
          setPage={setPage}
          appliedFilter={appliedFilter}
          setAppliedFilter={setAppliedFilter}
        />
      )}
      {viewLeadsModal && (
        <ViewLeads
          open={viewLeadsModal}
          onCancel={() => setViewLeadsModal(null)}
          data={viewLeadsModal}
        />
      )}
      {activityDrawer && (
        <Drawer
          width={490}
          title="Activity"
          placement="right"
          closable={true}
          prefixCls="activityCustomDrawer"
          onClose={() => dispatch(updateActivityDrawer(false))}
          open={activityDrawer}
          key="right">
          <LeadsActivity />
        </Drawer>
      )}
      <StickyBox>
        <FlexWrapper justify={canCreate ? 'space-between' : 'end'} gap="10px">
          {canCreate && (
            <Button prefixCls="antCustomBtn" onClick={handleAddProject}>
              + Add Lead
            </Button>
          )}

          <FlexWrapperBox align="center" gap="10px" wrap="nowrap">
            <Input
              prefixCls="antCustomInput"
              placeholder="Search here..."
              prefix={<SearchIconNew />}
              style={{ width: '250px' }}
              onChange={(e) => {
                setSearch(e?.target?.value.trim());
                setPage(1);
              }}
              allowClear
            />
            <FilterButton handleClick={setFilterDrawer} appliedFilter={appliedFilter} />
          </FlexWrapperBox>
        </FlexWrapper>
      </StickyBox>
      {search || loading || data.length > 0 ? (
        <Table
          loading={loading}
          prefixCls="antCustomTable"
          columns={columns}
          dataSource={data}
          pagination={false}
          onChange={(_, __, sorter) => {
            handleSorting(sorter);
          }}
          defaultSortOrder={sort.order}
          scroll={{ x: '100%' }}
        />
      ) : (
        <FlexWrapper margin="60px 20px">
          <FlexWrapper direction="column">
            <NoData />
            <Title level={5} style={{ margin: '10px 0 0' }}>
              No Leads
            </Title>
            <DarkText weight="400">No Leads created yet</DarkText>
            {canCreate && (
              <Button
                onClick={handleAddProject}
                prefixCls="antCustomBtn"
                style={{ margin: '10px 0' }}>
                + Add New Lead
              </Button>
            )}
          </FlexWrapper>
        </FlexWrapper>
      )}

      <PaginationBox>
        <Pagination
          current={page}
          prefixCls="custom-pagination"
          pageSize={pageSize}
          total={totalCount}
          onChange={handlePageChange}
          showSizeChanger={false}
          hideOnSinglePage
        />
      </PaginationBox>

      {createLead && (
        <CreateLeads
          open={createLead}
          close={() => setCreateLead(false)}
          editDetails={editDetails}
          handleLeadList={handleLeadList}
        />
      )}
    </div>
  );
}
export default Leads;

const FlexWrapperBox = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  align-items: center;
  @media (max-width: 668px) {
    width: 100%;
    display: grid;
    grid-template-columns: 70% 30%;
    gap: 5px;
  }
`;
